'use client';

import { useCart } from '@/features/checkout/CartProvider';
import FilterBar from '@/features/filter-bar/FilterBar';
import SearchBtn from '@/features/search-input/SearchBtn.jsx';
import { clientFetch } from '@/lib/api/clientFetch';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState, useTransition } from 'react';
import {
  Badge,
  Button,
  Image,
  Nav,
  Navbar,
  NavDropdown,
  Offcanvas,
} from 'react-bootstrap';
import '../../filter-bar/filterBar.scss';

import AdminNavMenu from './AdminNavMenu.jsx';
import {
  navBarVariants,
  navItemsStagger,
  navItemVariants,
} from './animations.js';
import LogoCluster from './LogoCluster.jsx';
import { getNavRouteFlags } from './navRouteFlags.js';
import { resolveHeaderMessage } from './resolveHeaderMessage.js';
import useUnreadInboxCount from './useUnreadInboxCount.js';

function buildCurrentPath(pathname, searchParams) {
  const query = searchParams?.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export default function NavbarSM({
  currentUser = null,
  locale,
  headerMessages = {},
  languageOptionLabels = {},
  categoriesTree = [],
  categoriesMessages = {},
  brandName,
}) {
  const reduceMotion = useReducedMotion();
  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();
  const router = useRouter();
  const [, startTransition] = useTransition();
  const { totalItems, hasHydrated, resetCart } = useCart();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [shopExpanded, setShopExpanded] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [isMobileSmall, setIsMobileSmall] = useState(true);

  const { isInHubRoute, isLandingRoute, isInShopRoute, isRestrictedScreen } =
    getNavRouteFlags(pathname);
  const isInAdminRoute = pathname.startsWith('/admin');
  const isAdmin = ['admin', 'superadmin'].includes(currentUser?.role);
  const displayName =
    currentUser?.hubProfile?.displayName || currentUser?.name || '';
  const profilePath = isInHubRoute ? '/hub/profile' : '/account';
  const cartCount = hasHydrated ? totalItems : 0;
  const currentPath = buildCurrentPath(pathname, searchParams);
  const { unreadCount, unreadBadgeText } = useUnreadInboxCount(isAdmin);

  const labels = useMemo(
    () => ({
      homeLink: resolveHeaderMessage(
        headerMessages,
        'aria.homeLink',
        'Go to homepage',
      ),
      languageSwitcher: resolveHeaderMessage(
        headerMessages,
        'aria.languageSwitcher',
        'Change language',
      ),
      shop: resolveHeaderMessage(headerMessages, 'nav.shop', 'Shop'),
      hub: resolveHeaderMessage(headerMessages, 'nav.hub', 'Hub'),
      cart: resolveHeaderMessage(headerMessages, 'nav.cart', 'Cart'),
      signIn: resolveHeaderMessage(headerMessages, 'nav.signin', 'Sign In'),
      searchTitle: resolveHeaderMessage(
        headerMessages,
        'search.searchTitle',
        'Search',
      ),
      searchPlaceholder: resolveHeaderMessage(
        headerMessages,
        'search.placeholder',
        'Search products...',
      ),
      profile: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.profile',
        'Profile',
      ),
      logout: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.logout',
        'Logout',
      ),
      logoutError: resolveHeaderMessage(
        headerMessages,
        'nav.logoutError',
        'Could not log out user! Something went wrong.',
      ),
      inbox: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.inbox',
        'Inbox',
      ),
    }),
    [headerMessages],
  );

  useEffect(() => {
    setShopExpanded(isInShopRoute);
  }, [isInShopRoute]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 509.98px)');
    const syncMobileSmall = () => setIsMobileSmall(mediaQuery.matches);

    syncMobileSmall();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncMobileSmall);
      return () => mediaQuery.removeEventListener('change', syncMobileSmall);
    }

    mediaQuery.addListener(syncMobileSmall);
    return () => mediaQuery.removeListener(syncMobileSmall);
  }, []);

  const showShopExtras =
    (isInShopRoute && shopExpanded && !isInHubRoute) || isInAdminRoute;
  const showHubShopBtn = isInHubRoute && !showShopExtras && !isInAdminRoute;

  const goToPath = (path) => {
    setShowSearchInput(false);
    setLogoutError('');
    startTransition(() => {
      router.push(path);
    });
  };

  const onShopClick = () => {
    if (!isInShopRoute) {
      setShopExpanded(true);
      goToPath('/shop');
      return;
    }

    setShopExpanded((current) => !current);
  };

  const handleLogoClick = (event) => {
    event?.preventDefault?.();

    if (isInShopRoute) {
      const nextPath =
        pathname === '/shop' && !searchParams?.toString() ? '/' : '/shop';
      setShopExpanded(nextPath === '/shop');
      goToPath(nextPath);
      return;
    }

    if (isInHubRoute) {
      goToPath(pathname === '/hub' ? '/' : '/hub');
      return;
    }

    if (pathname.startsWith('/account')) {
      setShopExpanded(true);
      goToPath('/shop');
      return;
    }

    goToPath('/');
  };

  const handleLogout = async () => {
    setLogoutError('');

    try {
      await clientFetch('/api/users/logout', {
        method: 'POST',
      });

      resetCart();
      setShowSearchInput(false);
      startTransition(() => {
        router.replace('/');
        router.refresh();
      });
    } catch {
      setLogoutError(labels.logoutError);
    }
  };

  const userLabel = displayName ? displayName.split(' ')[0] : labels.signIn;
  const cartLabel =
    cartCount > 0 ? `${labels.cart} (${cartCount})` : labels.cart;
  const isShopActive = isInShopRoute && !isInHubRoute;

  const handleHubClick = () => {
    setShopExpanded(false);
    goToPath('/hub');
  };

  const handleSearchClick = () => setShowSearchInput(true);

  const ShopExtras = () => (
    <div className="small-navbar__shop-extras d-flex align-items-center flex-shrink-1 gap-2">
      <AnimatePresence initial={false} mode="wait">
        {!showSearchInput ? (
          <motion.span
            key="search-btn-sm"
            variants={navItemVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            layout
            className="mt-2"
          >
            <SearchBtn
              onClick={handleSearchClick}
              showSearchInput={showSearchInput}
              shouldScroll={pathname === '/shop'}
              scrollTargetIds={[
                'latest-products',
                'search-results',
                'latest-products-header',
                'search-results-header',
              ]}
              ariaLabel={labels.searchTitle}
            />
          </motion.span>
        ) : null}
      </AnimatePresence>

      <Navbar className="flex-grow-0" expand="mmd" expanded>
        <Navbar.Collapse id="basic-navbar-nav">
          <motion.div
            variants={navItemsStagger}
            initial={reduceMotion ? false : 'hidden'}
            animate={reduceMotion ? false : 'show'}
            layout
            className={`w-100 d-flex justify-content-end align-items-center ${
              isMobileSmall ? 'gap-3' : 'gap-2'
            }`}
          >
            <motion.div variants={navItemVariants} layout>
              <Nav.Link
                onClick={() => {
                  goToPath('/cart');
                }}
                className="d-flex align-items-center px-0"
                aria-label={cartLabel}
                data-cart-anchor="true"
              >
                <motion.img
                  src="/icons/cart.png"
                  alt=""
                  width="26"
                  height="26"
                  className="cart-icon"
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
                {cartCount > 0 ? (
                  <Badge pill bg="warning" className="mt-3">
                    {cartCount}
                  </Badge>
                ) : null}
              </Nav.Link>
            </motion.div>

            {currentUser ? (
              <motion.div variants={navItemVariants} layout>
                <NavDropdown
                  title={
                    <span className="username-truncate" title={displayName}>
                      {userLabel}
                    </span>
                  }
                  id="username-sm"
                  className="ps-0 my-0"
                >
                  {isAdmin ? (
                    <>
                      <NavDropdown.Item
                        as={Link}
                        href="/admin/inbox"
                        className="d-flex align-items-center gap-2"
                      >
                        <span aria-hidden="true">📥</span>
                        <span className="dropdown-label flex-grow-1 position-relative">
                          {labels.inbox}
                          {unreadCount > 0 ? (
                            <span className="position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger">
                              {unreadBadgeText}
                            </span>
                          ) : null}
                        </span>
                      </NavDropdown.Item>
                      <AdminNavMenu messages={headerMessages} />
                      <NavDropdown.Divider />
                    </>
                  ) : null}
                  <NavDropdown.Item
                    as={Link}
                    href={profilePath}
                    className="d-flex justify-content-center align-items-center gap-2"
                  >
                    <span aria-hidden="true">👤</span>
                    <span className="dropdown-label flex-grow-1">
                      {labels.profile}
                    </span>
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as="button"
                    onClick={handleLogout}
                    className="d-flex justify-content-center align-items-center gap-2"
                  >
                    <span aria-hidden="true">🚪</span>
                    <span className="dropdown-label flex-grow-1">
                      {labels.logout}
                    </span>
                  </NavDropdown.Item>
                </NavDropdown>
              </motion.div>
            ) : (
              <motion.div variants={navItemVariants} layout>
                <Nav.Link
                  onClick={() => {
                    goToPath(
                      `/login?redirect=${encodeURIComponent(currentPath)}`,
                    );
                  }}
                  className="nav-link d-flex align-items-center"
                >
                  <span>
                    <Image
                      src="/icons/user-alt.png"
                      alt={labels.signIn}
                      width={32}
                      height={32}
                      className="login-icon"
                    />
                  </span>
                </Nav.Link>
              </motion.div>
            )}
          </motion.div>
        </Navbar.Collapse>
      </Navbar>
    </div>
  );

  return (
    <>
      <motion.nav
        className={`navbar navbar-dark small-navbar d-block ${
          showSearchInput ? 'search-active' : ''
        }`}
        variants={navBarVariants}
        initial={reduceMotion ? false : 'hidden'}
        animate={reduceMotion ? false : 'show'}
        layout
        style={{ padding: '0.8rem .5rem' }}
      >
        <div className="main-row d-flex align-items-center justify-content-between">
          {/* Left cluster (logo + langSwitcher) */}

          <div className="d-flex align-items-center gap-2 position-relative">
            <LogoCluster
              size="sm"
              rasterImageSrc="/icons/generic-game-desktop.png"
              onHomeClick={handleLogoClick}
              siteName={String(headerMessages.siteName || '').replace(
                '{{brandName}}',
                brandName,
              )}
              siteNameShort={headerMessages.siteNameShort || 'GameHub'}
              siteNameXs={headerMessages.siteNameXs || 'GameHub'}
              wordmarkPalette={
                locale === 'fr-SN' ? 'palette-country-sn' : 'palette-country-us'
              }
              showLangSwitcher={!isRestrictedScreen}
              currentLocale={locale}
              languageOptionLabels={languageOptionLabels}
              languageSwitcherAriaLabel={labels.languageSwitcher}
              homeAriaLabel={labels.homeLink}
              brandName={brandName}
            />
          </div>

          {showShopExtras || showHubShopBtn ? (
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={showShopExtras ? 'hub-btn-sm' : 'shop-btn-sm'}
                initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
                exit={{ ...navItemVariants.exit }}
                layout
              >
                {showShopExtras ? (
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => {
                      setShopExpanded(false);
                      goToPath('/hub');
                    }}
                    active={isInHubRoute}
                    className="nav-btn"
                  >
                    <span aria-hidden="true">🎮</span>
                    {labels.hub}
                  </Button>
                ) : (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={onShopClick}
                    active={isShopActive}
                    className="nav-btn"
                  >
                    <Image
                      src="/icons/store.png"
                      alt=""
                      width={18}
                      height={18}
                    />
                    <span>{labels.shop}</span>
                  </Button>
                )}
              </motion.span>
            </AnimatePresence>
          ) : null}

          {!showShopExtras && !isInAdminRoute ? (
            <motion.span
              variants={navItemVariants}
              initial={reduceMotion ? false : 'hidden'}
              animate={reduceMotion ? false : 'show'}
              layout
            >
              <div className="small-navbar__primary-nav d-flex gap-2 align-items-center mt-1 flex-wrap justify-content-end">
                {!isInHubRoute ? (
                  <Button
                    variant="info"
                    size="sm"
                    onClick={handleHubClick}
                    active={isInHubRoute}
                    className="nav-btn"
                  >
                    <span aria-hidden="true">🎮</span>
                    {labels.hub}
                  </Button>
                ) : null}

                {!isInShopRoute && !isInHubRoute ? (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={onShopClick}
                    active={(isLandingRoute || isShopActive) && !isInHubRoute}
                    className="nav-btn"
                  >
                    <Image
                      src="/icons/store.png"
                      alt=""
                      width={18}
                      height={18}
                    />
                    <span>{labels.shop}</span>
                  </Button>
                ) : null}

                {isInHubRoute ? (
                  currentUser ? (
                    <NavDropdown
                      title={
                        <span className="username-truncate" title={displayName}>
                          {userLabel}
                        </span>
                      }
                      id="username-hub-sm"
                      className="ps-0 my-0"
                    >
                      {isAdmin ? (
                        <>
                          <NavDropdown.Item
                            as={Link}
                            href="/admin/inbox"
                            className="d-flex align-items-center gap-2"
                          >
                            <span aria-hidden="true">📥</span>
                            <span className="dropdown-label flex-grow-1 position-relative">
                              {labels.inbox}
                              {unreadCount > 0 ? (
                                <span className="position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger">
                                  {unreadBadgeText}
                                </span>
                              ) : null}
                            </span>
                          </NavDropdown.Item>
                          <AdminNavMenu messages={headerMessages} />
                          <NavDropdown.Divider />
                        </>
                      ) : null}
                      <NavDropdown.Item
                        as={Link}
                        href={profilePath}
                        className="d-flex justify-content-center align-items-center gap-2"
                      >
                        <span aria-hidden="true">👤</span>
                        <span className="dropdown-label flex-grow-1">
                          {labels.profile}
                        </span>
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as="button"
                        onClick={handleLogout}
                        className="d-flex justify-content-center align-items-center gap-2"
                      >
                        <span aria-hidden="true">🚪</span>
                        <span className="dropdown-label flex-grow-1">
                          {labels.logout}
                        </span>
                      </NavDropdown.Item>
                    </NavDropdown>
                  ) : (
                    <Nav.Link
                      onClick={() => {
                        goToPath(
                          `/login?redirect=${encodeURIComponent(currentPath)}`,
                        );
                      }}
                      className="nav-link d-flex align-items-center"
                    >
                      <span>
                        <Image
                          src="/icons/user-alt.png"
                          alt={labels.signIn}
                          width={32}
                          height={32}
                          className="login-icon"
                        />
                      </span>
                    </Nav.Link>
                  )
                ) : null}
              </div>
            </motion.span>
          ) : null}

          {isInAdminRoute ? (
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key="shop-extras-mainrow"
                initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
                exit={{
                  opacity: 0,
                  y: reduceMotion ? 0 : -4,
                  pointerEvents: 'none',
                  transition: { duration: 0.18 },
                }}
                layout
              >
                <ShopExtras />
              </motion.div>
            </AnimatePresence>
          ) : !isMobileSmall && showShopExtras ? (
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key="shop-extras-mainrow"
                initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
                exit={{
                  opacity: 0,
                  y: reduceMotion ? 0 : -4,
                  pointerEvents: 'none',
                  transition: { duration: 0.18 },
                }}
                layout
              >
                <ShopExtras />
              </motion.div>
            </AnimatePresence>
          ) : null}
        </div>

        {!isInAdminRoute ? (
          <AnimatePresence initial={false} mode="popLayout">
            {isMobileSmall && showShopExtras ? (
              <motion.div
                key="shop-extras-secondaryrow"
                className="secondary-row d-flex justify-content-center mt-2"
                initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
                exit={{
                  opacity: 0,
                  y: reduceMotion ? 0 : -4,
                  transition: { duration: 0.18 },
                }}
                layout
              >
                <ShopExtras />
              </motion.div>
            ) : null}
          </AnimatePresence>
        ) : null}
      </motion.nav>

      {showShopExtras && showSearchInput ? (
        <Offcanvas
          show={showSearchInput}
          onHide={() => setShowSearchInput(false)}
          placement="top"
          className="search-offcanvas bg-landscape-surface-2"
          backdrop
          backdropClassName="search-offcanvas-backdrop"
          aria-labelledby="search-offcanvas-title"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="search-offcanvas-title">
              <h4 className="text-warning mb-0">{labels.searchTitle}</h4>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="pt-2 pb-3">
            <FilterBar
              categoriesTree={categoriesTree}
              pathname="/shop"
              onSubmit={() => setShowSearchInput(false)}
              onNavigate={() => setShowSearchInput(false)}
              filterMessages={categoriesMessages.filter || {}}
            />
            {logoutError ? (
              <div className="auth-message auth-message--error mt-3">
                {logoutError}
              </div>
            ) : null}
          </Offcanvas.Body>
        </Offcanvas>
      ) : null}
    </>
  );
}
